import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { IndianRupee, Percent, Wallet, Receipt, TrendingUp, Utensils, CalendarDays, Plus, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, PieChart, Pie, Cell } from "recharts";
import { api } from "@/lib/api";
import { rs, pct, qty, fmtDate, todayIso, monthOf, daysInMonth, fmtMonth } from "@/lib/format";
import { KpiCard, PageHeader, Panel } from "@/components/Kpi";
import { PaidBySplit } from "@/components/PaidBy";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export const useReport = (start, end) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/reports", { params: { start, end } }).then((r) => setData(r.data)).catch(() => setData(false));
  }, [start, end]);
  return data;
};

const Kpis = ({ s }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
    <KpiCard testId="gross-sales-kpi-card" label="Gross Sales" value={rs(s.gross)} sub={`${qty(s.plates)} plates`} icon={IndianRupee} tone="amber" />
    <KpiCard testId="deduction-kpi-card" label={`Deduction ${s.deduction_pct}%`} value={rs(s.deduction)} icon={Percent} tone="orange" delay={50} />
    <KpiCard testId="net-sales-kpi-card" label="Net Sales" value={rs(s.net)} sub={`${100 - s.deduction_pct}% retained`} icon={Wallet} tone="blue" delay={100} />
    <KpiCard testId="expenses-kpi-card" label="Total Expense" value={rs(s.total_expense)} sub={`Var ${rs(s.variable_expense)} · Fixed ${rs(s.fixed_cost)}`} icon={Receipt} tone="rose" delay={150} />
    <KpiCard testId="net-profit-kpi-card" label="Net Profit" value={rs(s.profit)} sub={`Margin ${pct(s.margin)}`} icon={TrendingUp} tone={s.profit >= 0 ? "emerald" : "rose"} delay={200} />
    <KpiCard testId="avg-day-kpi-card" label="Avg / Trading Day" value={rs(s.avg_per_day)} sub={`${s.trading_days} trading days`} icon={CalendarDays} delay={250} />
  </div>
);

const Trend = ({ days }) => (
  <ResponsiveContainer width="100%" height={280}>
    <AreaChart data={days.map((d) => ({ ...d, label: fmtDate(d.date) }))} margin={{ left: 0, right: 8, top: 8 }}>
      <defs>
        <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D97706" stopOpacity={0.35} /><stop offset="100%" stopColor="#D97706" stopOpacity={0} /></linearGradient>
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
      <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k`} width={40} />
      <Tooltip formatter={(v) => rs(v)} contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0" }} />
      <Area type="monotone" dataKey="day_sales" name="Day shift" stackId="1" stroke="#D97706" fill="url(#g1)" strokeWidth={2} />
      <Area type="monotone" dataKey="night_sales" name="Night shift" stackId="1" stroke="#1e293b" fill="#1e293b" fillOpacity={0.12} strokeWidth={2} />
    </AreaChart>
  </ResponsiveContainer>
);

const ShiftSplit = ({ s }) => {
  const data = [{ name: "Day", value: s.day_sales }, { name: "Night", value: s.night_sales }];
  return (
    <div className="flex items-center gap-6">
      <ResponsiveContainer width={150} height={150}>
        <PieChart><Pie data={data} dataKey="value" innerRadius={45} outerRadius={70} paddingAngle={2} stroke="none">
          <Cell fill="#D97706" /><Cell fill="#1e293b" /></Pie></PieChart>
      </ResponsiveContainer>
      <div className="space-y-4 text-sm">
        {data.map((d, i) => (
          <div key={d.name}>
            <div className="flex items-center gap-2 text-slate-500"><span className={`h-2.5 w-2.5 rounded-full ${i ? "bg-slate-800" : "bg-amber-600"}`} />{d.name} shift</div>
            <div className="num font-bold text-slate-900 text-lg">{rs(d.value)}</div>
            <div className="text-xs text-slate-500">{pct(s.gross ? d.value / s.gross : 0)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const TopItems = ({ items }) => {
  const top = [...items].sort((a, b) => b.sales - a.sales).slice(0, 6).filter((x) => x.sales > 0);
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={top} layout="vertical" margin={{ left: 0, right: 16 }}>
        <XAxis type="number" hide />
        <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11, fill: "#334155" }} tickLine={false} axisLine={false} />
        <Tooltip formatter={(v) => rs(v)} contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0" }} />
        <Bar dataKey="sales" fill="#D97706" radius={[0, 6, 6, 0]} barSize={18} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const Weekly = ({ weeks }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="weekly-table">
      <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b">
        <th className="py-2 pr-4">Week</th><th className="py-2 pr-4 text-right">Days</th><th className="py-2 pr-4 text-right">Plates</th>
        <th className="py-2 pr-4 text-right">Gross</th><th className="py-2 pr-4 text-right">Net</th><th className="py-2 text-right">Var. Exp</th></tr></thead>
      <tbody>{weeks.map((w) => (
        <tr key={w.week_start} className="border-b border-slate-100 hover:bg-amber-50/40 transition-colors">
          <td className="py-2.5 pr-4 font-medium text-slate-700">{fmtDate(w.week_start)} – {fmtDate(w.week_end)}</td>
          <td className="py-2.5 pr-4 text-right num">{w.trading_days}</td>
          <td className="py-2.5 pr-4 text-right num">{qty(w.plates)}</td>
          <td className="py-2.5 pr-4 text-right num font-semibold">{rs(w.gross)}</td>
          <td className="py-2.5 pr-4 text-right num">{rs(w.net)}</td>
          <td className="py-2.5 text-right num text-rose-700">{rs(w.variable_expense)}</td>
        </tr>))}</tbody>
    </table>
  </div>
);

const prevMonth = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
};

const ROWS = [
  ["Gross sales", "gross", rs, 1], ["Net sales", "net", rs, 1], ["Variable expense", "variable_expense", rs, -1],
  ["Fixed costs", "fixed_cost", rs, -1], ["Total expense", "total_expense", rs, -1], ["Net profit", "profit", rs, 1],
  ["Plates served", "plates", qty, 1], ["Trading days", "trading_days", (v) => v, 1], ["Avg sales / day", "avg_per_day", rs, 1],
];

const Change = ({ cur, prev, good }) => {
  const diff = cur - prev;
  if (!diff) return <span className="text-slate-400">—</span>;
  const up = diff > 0, positive = up === (good > 0);
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  const p = prev ? `${Math.abs((diff / Math.abs(prev)) * 100).toFixed(1)}%` : "new";
  return <span className={`inline-flex items-center gap-1 font-semibold ${positive ? "text-emerald-700" : "text-rose-700"}`}><Icon className="h-3.5 w-3.5" />{p}</span>;
};

const Compare = ({ cur, prev, month }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="month-comparison-table">
      <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b">
        <th className="py-2 pr-4">Metric</th><th className="py-2 pr-4 text-right">{fmtMonth(month)}</th>
        <th className="py-2 pr-4 text-right">{fmtMonth(prevMonth(month))}</th><th className="py-2 pr-4 text-right">Difference</th><th className="py-2 text-right">Change</th></tr></thead>
      <tbody>{ROWS.map(([label, k, f, good]) => (
        <tr key={k} className="border-b border-slate-100 hover:bg-amber-50/40 transition-colors" data-testid={`compare-row-${k}`}>
          <td className="py-2.5 pr-4 font-medium text-slate-700">{label}</td>
          <td className="py-2.5 pr-4 text-right num font-semibold">{f(cur[k])}</td>
          <td className="py-2.5 pr-4 text-right num text-slate-500">{f(prev[k])}</td>
          <td className="py-2.5 pr-4 text-right num">{cur[k] - prev[k] > 0 ? "+" : ""}{f(cur[k] - prev[k])}</td>
          <td className="py-2.5 text-right num"><Change cur={cur[k]} prev={prev[k]} good={good} /></td>
        </tr>))}</tbody>
    </table>
  </div>
);

export default function Dashboard() {
  const [month, setMonth] = useState(monthOf(todayIso()));
  useEffect(() => {
    api.get("/entries", { params: { month } }).then((r) => {
      if (!r.data.length) api.get("/entries", { params: { month: "2026-09" } }).then((x) => x.data.length && setMonth("2026-09"));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const data = useReport(`${month}-01`, `${month}-${daysInMonth(month)}`);
  const pm = prevMonth(month);
  const prev = useReport(`${pm}-01`, `${pm}-${daysInMonth(pm)}`);

  return (
    <div>
      <PageHeader title="Dashboard" subtitle={`Sales, expenses & profit — ${fmtMonth(month)}`}>
        <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-44 bg-white" data-testid="dashboard-month-picker" />
        <Button asChild className="bg-amber-600 hover:bg-amber-700" data-testid="dashboard-add-entry-button">
          <Link to="/entry"><Plus className="h-4 w-4 mr-1" />Today's entry</Link>
        </Button>
      </PageHeader>
      {!data ? <div className="text-slate-500" data-testid="dashboard-loading">{data === false ? "Could not load data" : "Loading…"}</div> : (
        <div className="space-y-6">
          <Kpis s={data.summary} />
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <Panel title="Daily sales trend" className="xl:col-span-2" testId="daily-trend-chart"><Trend days={data.days} /></Panel>
            <Panel title="Day vs Night" testId="shift-split-chart"><ShiftSplit s={data.summary} /></Panel>
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <Panel title="Top selling items" right={<Utensils className="h-4 w-4 text-slate-400" />} testId="top-items-chart"><TopItems items={data.items} /></Panel>
            <Panel title="Week by week" testId="weekly-panel"><Weekly weeks={data.weeks} /></Panel>
          </div>
          <Panel title="Expenses paid by — 1st–15th vs 16th–end" right={<Link to="/reports" className="text-xs text-amber-700 hover:underline" data-testid="paid-by-report-link">Full report</Link>} testId="paid-by-panel">
            <PaidBySplit paidBy={data.paid_by} />
          </Panel>
          <Panel title={`This month vs last month`} testId="month-comparison-panel">
            {prev ? <Compare cur={data.summary} prev={prev.summary} month={month} /> : <div className="text-slate-500">Loading…</div>}
          </Panel>
        </div>
      )}
    </div>
  );
}
